import { Box, BoxProps } from '@chakra-ui/core'
import React from 'react'
import { PieChart, Pie, Sector, ResponsiveContainer, Legend } from 'recharts'
import { randomColor } from '../../../../utils'
import { ChartProps } from './type'
import { STANDARD_GRAPH_HEIGHT } from './constants'

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value
  } = props
  const sin = Math.sin(-RADIAN * midAngle)
  const cos = Math.cos(-RADIAN * midAngle)
  const sx = cx + (outerRadius + 10) * cos
  const sy = cy + (outerRadius + 10) * sin
  const mx = cx + (outerRadius + 30) * cos
  const my = cy + (outerRadius + 30) * sin
  const ex = mx + (cos >= 0 ? 1 : -1) * 22
  const ey = my
  const textAnchor = cos >= 0 ? 'start' : 'end'

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />

      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
      >{`${value}`}</text>
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
        {`(Percentage ${(percent * 100).toFixed(2)}%)`}
      </text>
      <text x={cx} y={cy + 120} dy={8} textAnchor="middle" fill={fill}>
        {payload.name}
      </text>
    </g>
  )
}

type PieChartComponentProps = ChartProps & BoxProps

const PieChartComponent: React.FC<PieChartComponentProps> = ({
  data,
  xKey,
  yKey,
  barColor,
  randomizeColors,
  ...rest
}) => {
  const [activeIndex, setActiveIndex] = React.useState(0)
  const processedData = React.useMemo(() => {
    const results = data.map((entry) => ({
      ...entry,
      fill: randomizeColors ? randomColor() : barColor
    }))
    return results
  }, [data, randomizeColors, barColor])
  const onPieEnter = (_data: any, index: number) => {
    setActiveIndex(index)
  }

  return (
    <Box w="100%" h="100%" fontSize="xs" {...rest}>
      <ResponsiveContainer width="100%" height={STANDARD_GRAPH_HEIGHT}>
        <PieChart>
          <Pie
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            data={processedData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            fill={barColor}
            dataKey={yKey}
            onMouseEnter={onPieEnter}
          />
          <Legend
            iconType="circle"
            formatter={(value) => (
              <span>
                {value}{' '}
                <b>
                  {processedData.find((item) => item[xKey] === value)?.[yKey]
                    ? ` (${processedData.find((item) => item[xKey] === value)?.[yKey]})`
                    : ''}
                </b>
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  )
}

PieChartComponent.defaultProps = {
  randomizeColors: true
}

export default PieChartComponent
